import React from 'react';
import { 
  Container, 
  Grid, 
  Typography, 
  Box, 
  Card, 
  CardContent,
  Chip,
  LinearProgress,
  useTheme,
  alpha,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { 
  TrendingUp, 
  AttachMoney, 
  ShoppingCart,
  People,
  ShowChart,
  Analytics,
  Assessment,
  Timeline,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  DateRange,
  Insights
} from '@mui/icons-material';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  LineChart,
  Line,
  Area,
  AreaChart,
  PieChart,
  Pie,
  Cell,
  ComposedChart
} from 'recharts';
import { useStoreStats, useDailySales } from '../hooks/useApiWithStore';
import PremiumCard from '../components/ui/PremiumCard';
import PageHeader from '../components/ui/PageHeader';
import { DESIGN_SYSTEM } from '../theme/designSystem';

/**
 * Página de Estadísticas Completamente Rediseñada
 * Gráficos hermosos, datos útiles, sin espacios vacíos
 */
export default function StatsNew() {
  const theme = useTheme();
  const { data: statsData, isLoading: statsLoading } = useStoreStats();
  const { data: dailySalesData, isLoading: salesLoading } = useDailySales();

  // Procesar datos de ventas diarias
  const processedSalesData = React.useMemo(() => {
    if (!dailySalesData?.data) return [];
    
    return dailySalesData.data.map(item => ({
      fecha: new Date(item.fecha).toLocaleDateString('es-ES', { 
        month: 'short', 
        day: 'numeric' 
      }),
      ventas: parseFloat(item.ventas) || 0,
      ordenes: parseInt(item.ordenes) || 0,
      ticketPromedio: parseFloat(item.ventas) / Math.max(parseInt(item.ordenes), 1) || 0
    }));
  }, [dailySalesData]);

  // Datos de ejemplo para embudo de ventas
  const salesFunnelData = [
    { stage: 'Visitantes', value: 10000, color: theme.palette.info.main },
    { stage: 'Carritos', value: 1500, color: theme.palette.warning.main },
    { stage: 'Checkouts', value: 800, color: theme.palette.secondary.main },
    { stage: 'Órdenes', value: 450, color: theme.palette.success.main },
    { stage: 'Órdenes pagadas', value: 420, color: theme.palette.primary.main }
  ];

  // Datos de rendimiento por hora
  const hourlyData = [
    { hour: '00:00', orders: 2, sales: 150 },
    { hour: '06:00', orders: 5, sales: 320 },
    { hour: '12:00', orders: 15, sales: 890 },
    { hour: '18:00', orders: 25, sales: 1200 },
    { hour: '21:00', orders: 18, sales: 950 }
  ];

  // Métricas calculadas
  const metrics = React.useMemo(() => {
    const totalSales = statsData?.totalSales || 0;
    const totalOrders = statsData?.totalOrders || 0;
    const avgTicket = totalOrders > 0 ? totalSales / totalOrders : 0;
    const conversionRate = statsData?.conversionRate || 0;
    
    return {
      totalSales,
      totalOrders,
      avgTicket,
      conversionRate,
      newCustomers: statsData?.newCustomers || 0,
      repeatCustomers: statsData?.repeatCustomers || 0
    };
  }, [statsData]);

  if (statsLoading || salesLoading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box display="flex" alignItems="center" justifyContent="center" minHeight="60vh">
          <Box textAlign="center">
            <LinearProgress sx={{ width: 200, mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Cargando estadísticas avanzadas...
            </Typography>
          </Box>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header Unificado */}
      <PageHeader 
        title={DESIGN_SYSTEM.pageHeaders.stats.title}
        subtitle={DESIGN_SYSTEM.pageHeaders.stats.subtitle}
        icon="📊"
      />

      {/* Métricas principales */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <PremiumCard
            title="Órdenes"
            icon={<ShoppingCart />}
            badge="Tiempo Real"
          >
            <Box textAlign="center" py={2}>
              <Typography variant="h3" fontWeight={700} color="primary.main">
                {metrics.totalOrders}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total de órdenes
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={75} 
                sx={{ mt: 1, height: 6, borderRadius: 3 }}
              />
            </Box>
          </PremiumCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <PremiumCard
            title="Ventas"
            icon={<AttachMoney />}
            badge="Premium"
          >
            <Box textAlign="center" py={2}>
              <Typography variant="h3" fontWeight={700} color="success.main">
                ${metrics.totalSales.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Revenue total
              </Typography>
              <Chip 
                label={`+${(metrics.totalSales * 0.1).toLocaleString()}`} 
                color="success" 
                size="small" 
                sx={{ mt: 1 }}
              />
            </Box>
          </PremiumCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <PremiumCard
            title="Ticket Promedio"
            icon={<TrendingUp />}
            badge="Nuevo"
          >
            <Box textAlign="center" py={2}>
              <Typography variant="h3" fontWeight={700} color="info.main">
                ${metrics.avgTicket.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Por orden
              </Typography>
              <Typography variant="caption" color="info.main">
                {metrics.avgTicket > 100 ? '↗ Excelente' : '→ Promedio'}
              </Typography>
            </Box>
          </PremiumCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <PremiumCard
            title="Conversión"
            icon={<Assessment />}
            badge="IA"
          >
            <Box textAlign="center" py={2}>
              <Typography variant="h3" fontWeight={700} color="warning.main">
                {metrics.conversionRate.toFixed(1)}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Tasa de conversión
              </Typography>
              <Typography variant="caption" color="warning.main">
                {metrics.conversionRate > 2 ? '↗ Muy buena' : '→ Mejorable'}
              </Typography>
            </Box>
          </PremiumCard>
        </Grid>
      </Grid>

      {/* Gráficos principales */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={8}>
          <PremiumCard
            title="📈 Ventas Diarias"
            subtitle="Evolución de ventas en el tiempo"
            icon={<Timeline />}
          >
            <Box sx={{ height: 350 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={processedSalesData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.text.secondary, 0.1)} />
                  <XAxis 
                    dataKey="fecha" 
                    tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                  />
                  <YAxis 
                    yAxisId="left"
                    tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                    tickFormatter={(value) => `$${value.toLocaleString()}`}
                  />
                  <YAxis 
                    yAxisId="right" 
                    orientation="right"
                    tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                  />
                  <Tooltip 
                    formatter={(value, name) => [
                      name === 'ventas' ? `$${value.toLocaleString()}` : value,
                      name === 'ventas' ? 'Ventas' : 'Órdenes'
                    ]}
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      borderRadius: 8,
                      boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.12)}`
                    }}
                  />
                  <Area 
                    yAxisId="left"
                    type="monotone" 
                    dataKey="ventas" 
                    fill={alpha(theme.palette.primary.main, 0.3)}
                    stroke={theme.palette.primary.main}
                    strokeWidth={3}
                  />
                  <Bar 
                    yAxisId="right"
                    dataKey="ordenes" 
                    fill={alpha(theme.palette.success.main, 0.7)}
                    radius={[4, 4, 0, 0]}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </PremiumCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <PremiumCard
            title="🔄 Embudo de Ventas"
            subtitle="Conversión paso a paso"
            icon={<PieChartIcon />}
          >
            <Box sx={{ height: 350 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={salesFunnelData} layout="horizontal">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis 
                    type="category" 
                    dataKey="stage" 
                    tick={{ fontSize: 11 }}
                    width={80}
                  />
                  <Tooltip 
                    formatter={(value) => [value.toLocaleString(), 'Cantidad']}
                  />
                  <Bar 
                    dataKey="value" 
                    fill={theme.palette.primary.main}
                    radius={[0, 4, 4, 0]}
                  >
                    {salesFunnelData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </PremiumCard>
        </Grid>
      </Grid>

      {/* Análisis detallado */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={6}>
          <PremiumCard
            title="⏰ Rendimiento por Hora"
            subtitle="Patrones de comportamiento"
            icon={<DateRange />}
          >
            <Box sx={{ height: 250 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={hourlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.text.secondary, 0.1)} />
                  <XAxis dataKey="hour" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Line 
                    type="monotone" 
                    dataKey="orders" 
                    stroke={theme.palette.primary.main}
                    strokeWidth={3}
                    dot={{ fill: theme.palette.primary.main, strokeWidth: 2, r: 4 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="sales" 
                    stroke={theme.palette.success.main}
                    strokeWidth={3}
                    dot={{ fill: theme.palette.success.main, strokeWidth: 2, r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </PremiumCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <PremiumCard
            title="👥 Análisis de Clientes"
            subtitle="Segmentación y comportamiento"
            icon={<People />}
          >
            <Box py={2}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight={600}>
                  Nuevos Clientes
                </Typography>
                <Typography variant="h4" fontWeight={700} color="primary.main">
                  {metrics.newCustomers}
                </Typography>
              </Box>
              
              <LinearProgress 
                variant="determinate" 
                value={65} 
                sx={{ height: 8, borderRadius: 4, mb: 2 }}
              />
              
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight={600}>
                  Clientes Recurrentes
                </Typography>
                <Typography variant="h4" fontWeight={700} color="success.main">
                  {metrics.repeatCustomers}
                </Typography>
              </Box>
              
              <LinearProgress 
                variant="determinate" 
                value={35} 
                color="success"
                sx={{ height: 8, borderRadius: 4, mb: 2 }}
              />

              <Divider sx={{ my: 2 }} />

              <Box textAlign="center">
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Ratio de Retención
                </Typography>
                <Typography variant="h5" fontWeight={700} color="info.main">
                  {metrics.repeatCustomers > 0 ? 
                    ((metrics.repeatCustomers / (metrics.newCustomers + metrics.repeatCustomers)) * 100).toFixed(1) : 0}%
                </Typography>
              </Box>
            </Box>
          </PremiumCard>
        </Grid>
      </Grid>

      {/* Tabla de datos detallados */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <PremiumCard
            title="📋 Datos Detallados"
            subtitle="Información completa del período"
            icon={<Analytics />}
            badge="Completo"
          >
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Fecha</strong></TableCell>
                    <TableCell align="right"><strong>Órdenes</strong></TableCell>
                    <TableCell align="right"><strong>Ventas</strong></TableCell>
                    <TableCell align="right"><strong>Ticket Promedio</strong></TableCell>
                    <TableCell align="right"><strong>Estado</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {processedSalesData.slice(0, 5).map((row, index) => (
                    <TableRow key={index} hover>
                      <TableCell>{row.fecha}</TableCell>
                      <TableCell align="right">{row.ordenes}</TableCell>
                      <TableCell align="right">${row.ventas.toLocaleString()}</TableCell>
                      <TableCell align="right">${row.ticketPromedio.toLocaleString()}</TableCell>
                      <TableCell align="right">
                        <Chip 
                          label={row.ventas > 1000 ? 'Excelente' : row.ventas > 500 ? 'Bueno' : 'Regular'} 
                          color={row.ventas > 1000 ? 'success' : row.ventas > 500 ? 'info' : 'warning'}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </PremiumCard>
        </Grid>
      </Grid>
    </Container>
  );
}
